import { useState, useEffect } from 'react';
import { api, resolveImageUrl } from '@/lib/axios';
import { Filament } from '@/types';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Package,
  Layers,
  Search,
  Plus,
  Minus,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

interface ProductInventoryItem {
  id: number;
  name: string;
  slug?: string;
  sku?: string;
  categoryName: string;
  salePrice: number;
  costPrice: number;
  stockQuantity: number;
  manageStock: boolean;
  allowBackorder: boolean;
  estimatedDeliveryDays: number;
  isPublic: boolean;
  mainImageUrl?: string;
  status: 'in_stock' | 'on_demand' | 'out_of_stock';
}

interface InventorySummary {
  totalProducts: number;
  inStockProducts: number;
  onDemandProducts: number;
  outOfStockProducts: number;
  totalFilaments: number;
  totalFilamentsKg: number;
  lowStockFilaments: number;
}

interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export default function InventoryPage({ defaultTab = 'products' }: { defaultTab?: 'products' | 'filaments' }) {
  const [activeTab, setActiveTab] = useState<'products' | 'filaments'>(defaultTab);

  // --- Estado Productos ---
  const [products, setProducts] = useState<ProductInventoryItem[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [summary, setSummary] = useState<InventorySummary | null>(null);

  // --- Estado Filamentos ---
  const [filamentsData, setFilamentsData] = useState<PaginatedResponse<Filament>>({
    items: [],
    page: 1,
    pageSize: 8,
    totalCount: 0,
    totalPages: 1,
  });
  const [filamentSearch, setFilamentSearch] = useState('');
  const [isLoadingFilaments, setIsLoadingFilaments] = useState(false);
  const [isFilamentModalOpen, setIsFilamentModalOpen] = useState(false);
  const [newFilament, setNewFilament] = useState<Partial<Filament>>({
    brand: 'Grilon3',
    material: 'PLA',
    color_name: '',
    color_hex: '#B85D3B',
    weight_g: 1000,
    remaining_g: 1000,
    cost_per_kg: 8500,
  });

  useEffect(() => {
    loadProducts();
    loadSummary();
  }, [productSearch]);

  useEffect(() => {
    if (activeTab === 'filaments') {
      loadFilaments(1);
    }
  }, [activeTab, filamentSearch]);

  const loadSummary = async () => {
    try {
      const res = await api.get<InventorySummary>('/inventory/summary');
      setSummary(res.data);
    } catch {}
  };

  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const res = await api.get<ProductInventoryItem[]>('/inventory/products', {
        params: { search: productSearch },
      });
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Error al cargar inventario de productos');
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleAdjustStock = async (productId: number, adjustment: number) => {
    try {
      const res = await api.patch<{ stockQuantity: number; status: ProductInventoryItem['status'] }>(
        `/inventory/products/${productId}/stock`,
        { adjustment }
      );
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, stockQuantity: res.data.stockQuantity, status: res.data.status }
            : p
        )
      );
      loadSummary();
    } catch {
      toast.error('No se pudo actualizar el stock');
    }
  };

  const handleUpdateDeliveryDays = async (productId: number, days: number) => {
    if (days < 1) return;
    try {
      await api.patch(`/inventory/products/${productId}/stock`, {
        estimatedDeliveryDays: days,
      });
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, estimatedDeliveryDays: days } : p))
      );
      toast.success('Tiempo de envío actualizado');
    } catch {
      toast.error('Error al actualizar tiempo de envío');
    }
  };

  const handleToggleBackorder = async (productId: number, current: boolean) => {
    try {
      const next = !current;
      const res = await api.patch<{ status: ProductInventoryItem['status'] }>(
        `/inventory/products/${productId}/stock`,
        { allowBackorder: next }
      );
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, allowBackorder: next, status: res.data.status } : p
        )
      );
      loadSummary();
      toast.success(next ? 'Permite pedidos a demanda' : 'Marcado como no disponible sin stock');
    } catch {
      toast.error('Error al actualizar modo de encargo');
    }
  };

  // --- Filamentos handlers ---
  const loadFilaments = async (pageNumber: number) => {
    try {
      setIsLoadingFilaments(true);
      const res = await api.get<PaginatedResponse<any>>('/inventory', {
        params: {
          search: filamentSearch,
          page: pageNumber,
          pageSize: 8,
        },
      });
      const mappedItems = (res.data.items || []).map((f: any) => ({
        id: f.id.toString(),
        brand: f.brand,
        material: f.material,
        color_name: f.color,
        color_hex: f.colorHex || '#B85D3B',
        weight_g: Number(f.weightTotalG || 1000),
        remaining_g: Number(f.weightRemainingG || 0),
        cost_per_kg: Number(f.costPerKg || 0),
      }));
      setFilamentsData({
        ...res.data,
        items: mappedItems,
      });
    } catch {
      toast.error('Error al cargar filamentos');
    } finally {
      setIsLoadingFilaments(false);
    }
  };

  const handleCreateFilament = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFilament.brand || !newFilament.color_name) {
      toast.error('Marca y Color son obligatorios');
      return;
    }

    try {
      await api.post('/filaments', {
        brand: newFilament.brand,
        material: newFilament.material,
        color: newFilament.color_name,
        colorHex: newFilament.color_hex,
        weightTotalG: Number(newFilament.weight_g || 1000),
        weightRemainingG: Number(newFilament.remaining_g || 1000),
        costPerKg: Number(newFilament.cost_per_kg || 0),
      });
      toast.success('Filamento registrado en inventario');
      setIsFilamentModalOpen(false);
      loadFilaments(1);
      loadSummary();
    } catch {
      toast.error('Error al registrar filamento');
    }
  };

  const handleExportCsv = () => {
    const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    window.open(`${backendUrl}/inventory/export/csv`, '_blank');
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventario & Stock</h1>
          <p className="text-muted-foreground text-sm">
            Control de existencias físicas para la tienda online, tiempos de despacho e insumos 3D.
          </p>
        </div>

        {/* Tab Selector Buttons */}
        <div className="flex bg-muted p-1 rounded-xl border border-border/60">
          <button
            type="button"
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === 'products'
                ? 'bg-card text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Package className="h-4 w-4" />
            Stock de Productos (Tienda)
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('filaments')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === 'filaments'
                ? 'bg-card text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Layers className="h-4 w-4" />
            Filamentos & Insumos 3D
          </button>
        </div>
      </div>

      {/* KPI Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-border/80">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Total Catálogo</span>
            <Package className="h-4 w-4 text-primary" />
          </div>
          <div className="text-2xl font-bold mt-2">{summary?.totalProducts ?? products.length}</div>
          <p className="text-[11px] text-muted-foreground mt-0.5">Modelos registrados</p>
        </Card>

        <Card className="p-4 border-emerald-500/30 bg-emerald-500/5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">En Stock Físico</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="text-2xl font-bold mt-2 text-emerald-600 dark:text-emerald-400">
            {summary?.inStockProducts ?? products.filter((p) => p.stockQuantity > 0).length}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">Listos para entrega inmediata</p>
        </Card>

        <Card className="p-4 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Bajo Demanda</span>
            <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="text-2xl font-bold mt-2 text-amber-600 dark:text-amber-400">
            {summary?.onDemandProducts ?? products.filter((p) => p.stockQuantity === 0 && p.allowBackorder).length}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">Se imprimen por encargo</p>
        </Card>

        <Card className="p-4 border-border/80">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Insumos 3D</span>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold mt-2">
            {summary?.totalFilamentsKg ? `${summary.totalFilamentsKg} kg` : `${summary?.totalFilaments ?? 0} bobinas`}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {summary?.lowStockFilaments ? `⚠️ ${summary.lowStockFilaments} con stock bajo` : 'Stock de filamento OK'}
          </p>
        </Card>
      </div>

      {/* --- TAB 1: PRODUCTOS TERMINADOS --- */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o SKU..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="pl-9 text-sm"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={loadProducts}
              disabled={isLoadingProducts}
              className="gap-1.5"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingProducts ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          <Card className="border-border/80 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/40 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <tr>
                    <th className="py-3 px-4">Producto</th>
                    <th className="py-3 px-3">Precio Venta</th>
                    <th className="py-3 px-4 text-center">Stock Físico Inmediato</th>
                    <th className="py-3 px-4 text-center">Tiempo Envío (Días)</th>
                    <th className="py-3 px-4 text-center">Bajo Demanda</th>
                    <th className="py-3 px-4">Estado en Tienda</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {isLoadingProducts ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">
                        <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 opacity-50" />
                        Cargando inventario de productos...
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">
                        No se encontraron productos coincidentes.
                      </td>
                    </tr>
                  ) : (
                    products.map((p) => {
                      const hasStock = p.stockQuantity > 0;
                      return (
                        <tr key={p.id} className="hover:bg-muted/20 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {p.mainImageUrl ? (
                                <img
                                  src={resolveImageUrl(p.mainImageUrl)}
                                  alt={p.name}
                                  className="w-10 h-10 rounded-lg object-cover border border-border/70 shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-xs font-mono font-bold text-muted-foreground shrink-0">
                                  3D
                                </div>
                              )}
                              <div className="min-w-0">
                                <a
                                  href={`/products-admin/${p.id}`}
                                  className="font-medium text-foreground hover:text-primary transition-colors truncate block"
                                >
                                  {p.name}
                                </a>
                                <p className="text-[11px] text-muted-foreground">
                                  {p.categoryName} {p.sku ? `• SKU: ${p.sku}` : ''}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-3 font-semibold text-foreground">
                            ${p.salePrice.toLocaleString('es-AR')}
                          </td>

                          {/* Ajuste rápido de Stock */}
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 rounded-md"
                                onClick={() => handleAdjustStock(p.id, -1)}
                                disabled={p.stockQuantity <= 0}
                                title="Restar 1 unidad"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </Button>

                              <span
                                className={`font-mono text-sm font-bold min-w-[28px] text-center ${
                                  hasStock ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                                }`}
                              >
                                {p.stockQuantity}
                              </span>

                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 rounded-md"
                                onClick={() => handleAdjustStock(p.id, 1)}
                                title="Sumar 1 unidad terminada"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>

                          {/* Tiempo de Envío / Fabricación */}
                          <td className="py-3 px-4 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <Input
                                type="number"
                                min="1"
                                className="w-16 h-7 text-xs text-center font-mono"
                                defaultValue={p.estimatedDeliveryDays || 5}
                                onBlur={(e) =>
                                  handleUpdateDeliveryDays(p.id, Number(e.target.value))
                                }
                              />
                              <span className="text-xs text-muted-foreground">días</span>
                            </div>
                          </td>

                          {/* Toggle Bajo Demanda */}
                          <td className="py-3 px-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleToggleBackorder(p.id, p.allowBackorder)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                                p.allowBackorder
                                  ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 font-medium'
                                  : 'bg-muted text-muted-foreground border-border/60'
                              }`}
                              title="Permitir comprar aunque no haya stock físico listo"
                            >
                              {p.allowBackorder ? 'Permitido' : 'Solo Stock'}
                            </button>
                          </td>

                          {/* Estado resultante en la tienda online */}
                          <td className="py-3 px-4">
                            {hasStock ? (
                              <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-[11px] font-medium">
                                <CheckCircle2 className="h-3 w-3" /> En Stock ({p.stockQuantity})
                              </Badge>
                            ) : p.allowBackorder ? (
                              <Badge
                                variant="outline"
                                className="text-amber-600 border-amber-500/40 bg-amber-500/5 gap-1 text-[11px]"
                              >
                                <Clock className="h-3 w-3" /> A pedido ({p.estimatedDeliveryDays || 5}d)
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="gap-1 text-[11px]">
                                <AlertTriangle className="h-3 w-3" /> Agotado
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* --- TAB 2: FILAMENTOS & INSUMOS 3D --- */}
      {activeTab === 'filaments' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por marca o color..."
                value={filamentSearch}
                onChange={(e) => setFilamentSearch(e.target.value)}
                className="pl-9 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCsv} className="gap-1.5">
                <Download className="h-4 w-4" /> Exportar CSV
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setNewFilament({
                    brand: 'Grilon3',
                    material: 'PLA',
                    color_name: '',
                    color_hex: '#B85D3B',
                    weight_g: 1000,
                    remaining_g: 1000,
                    cost_per_kg: 8500,
                  });
                  setIsFilamentModalOpen(true);
                }}
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" /> Nueva Bobina
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {isLoadingFilaments ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-50" />
                Cargando bobinas de filamento...
              </div>
            ) : filamentsData.items.length === 0 ? (
              <Card className="col-span-full p-8 text-center border-dashed">
                <p className="text-muted-foreground">No hay bobinas registradas con ese criterio.</p>
              </Card>
            ) : (
              filamentsData.items.map((f) => {
                const percentage = Math.round((f.remaining_g / f.weight_g) * 100);
                const isLow = f.remaining_g < 200;

                return (
                  <Card key={f.id} className="border-border/80 hover:border-primary/40 transition-all">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-bold text-base leading-snug">{f.brand}</h4>
                          <p className="text-xs text-muted-foreground">{f.material}</p>
                        </div>
                        <div
                          className="w-5 h-5 rounded-full border shadow-xs"
                          style={{ backgroundColor: f.color_hex }}
                          title={f.color_name}
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="font-medium text-foreground">{f.color_name}</span>
                          <span className="font-mono text-muted-foreground">
                            {f.remaining_g}g / {f.weight_g}g
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              isLow ? 'bg-amber-500' : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(100, percentage)}%` }}
                          />
                        </div>
                      </div>

                      <div className="pt-2 border-t border-border/40 flex justify-between items-center text-xs">
                        <span className="text-muted-foreground">
                          ${f.cost_per_kg?.toLocaleString('es-AR')}/kg
                        </span>
                        {isLow && (
                          <Badge variant="outline" className="text-amber-600 border-amber-500/40 text-[10px] px-1.5 py-0 bg-amber-500/5">
                            Stock Bajo
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* Paginación Filamentos */}
          {filamentsData.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border/60">
              <span className="text-xs text-muted-foreground">
                Página {filamentsData.page} de {filamentsData.totalPages} ({filamentsData.totalCount} bobinas)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadFilaments(filamentsData.page - 1)}
                  disabled={filamentsData.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadFilaments(filamentsData.page + 1)}
                  disabled={filamentsData.page >= filamentsData.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Registrar Filamento */}
      <Dialog open={isFilamentModalOpen} onOpenChange={setIsFilamentModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Bobina de Filamento</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateFilament} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Marca *</Label>
                <Input
                  required
                  placeholder="Grilon3, Printalot, GST3D..."
                  value={newFilament.brand || ''}
                  onChange={(e) => setNewFilament({ ...newFilament, brand: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Material *</Label>
                <Input
                  required
                  placeholder="PLA, PETG, ABS..."
                  value={newFilament.material || ''}
                  onChange={(e) => setNewFilament({ ...newFilament, material: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nombre del Color *</Label>
                <Input
                  required
                  placeholder="Ej. Blanco Hueso"
                  value={newFilament.color_name || ''}
                  onChange={(e) => setNewFilament({ ...newFilament, color_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tono Hex</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    className="h-9 w-12 rounded border p-0.5 cursor-pointer shrink-0"
                    value={newFilament.color_hex || '#B85D3B'}
                    onChange={(e) => setNewFilament({ ...newFilament, color_hex: e.target.value })}
                  />
                  <Input
                    className="font-mono text-xs uppercase"
                    value={newFilament.color_hex || '#B85D3B'}
                    onChange={(e) => setNewFilament({ ...newFilament, color_hex: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Peso Total (g)</Label>
                <Input
                  type="number"
                  value={newFilament.weight_g || 1000}
                  onChange={(e) => setNewFilament({ ...newFilament, weight_g: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Restante (g)</Label>
                <Input
                  type="number"
                  value={newFilament.remaining_g || 1000}
                  onChange={(e) => setNewFilament({ ...newFilament, remaining_g: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Costo / kg ($)</Label>
                <Input
                  type="number"
                  value={newFilament.cost_per_kg || 8500}
                  onChange={(e) => setNewFilament({ ...newFilament, cost_per_kg: Number(e.target.value) })}
                />
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setIsFilamentModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar Bobina</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
